import { ContractTransaction } from '@ethersproject/contracts'
import { InterfaceEventName } from '@uniswap/analytics-events'
import { useWeb3React } from '@web3-react/core'
import { sendAnalyticsEvent, useTrace } from 'analytics'
import { ChainId, CurrencyAmount, MaxUint256, Token } from 'eth-mainnet-few-sdk-core-2'
import { ADDRESS_ZERO } from 'eth-mainnet-few-v3-sdk-2'
import { FEW_WRAPPED_TOKEN_FACTORY_ADDRESS } from 'eth-mainnet-universal-router-sdk-3'
import { useFewFactoryContract, useTokenContract } from 'hooks/useContract'
import { useSingleCallResult } from 'lib/hooks/multicall'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ApproveTransactionInfo, TransactionType } from 'state/transactions/types'
import { UserRejectedRequestError } from 'utils/errors'
import { didUserReject } from 'utils/swapErrorToUserReadableMessage'

const MAX_ALLOWANCE = MaxUint256.toString()

export function useTokenAllowance(
  token?: Token,
  owner?: string,
  spender?: string
): {
  tokenAllowance?: CurrencyAmount<Token>
  isSyncing: boolean
} {
  const contract = useTokenContract(token?.address, false)
  const inputs = useMemo(() => [owner, spender], [owner, spender])

  // If there is no allowance yet, re-check next observed block.
  // This guarantees that the tokenAllowance is marked isSyncing upon approval and updated upon being synced.
  const [blocksPerFetch, setBlocksPerFetch] = useState<1>()
  const { result, syncing: isSyncing } = useSingleCallResult(contract, 'allowance', inputs, { blocksPerFetch }) as {
    result?: Awaited<ReturnType<NonNullable<typeof contract>['allowance']>>
    syncing: boolean
  }

  const rawAmount = result?.toString() // convert to a string before using in a hook, to avoid spurious rerenders
  const allowance = useMemo(
    () => (token && rawAmount ? CurrencyAmount.fromRawAmount(token, rawAmount) : undefined),
    [token, rawAmount]
  )
  useEffect(() => setBlocksPerFetch(allowance?.equalTo(0) ? 1 : undefined), [allowance])

  return useMemo(() => ({ tokenAllowance: allowance, isSyncing }), [allowance, isSyncing])
}

export function useGetFewWrappedToken(token?: Token): {
  fewWrappedTokenAddress?: string
  isSyncing: boolean
} {
  const { chainId } = useWeb3React()

  const contract = useFewFactoryContract(FEW_WRAPPED_TOKEN_FACTORY_ADDRESS(chainId ?? ChainId.BLAST), false)
  const inputs = useMemo(() => [token?.address], [token?.address])

  // If there is no allowance yet, re-check next observed block.
  // This guarantees that the fewWrappedTokenAddress is marked isSyncing upon approval and updated upon being synced.
  const [blocksPerFetch, setBlocksPerFetch] = useState<1>()
  const { result, syncing: isSyncing } = useSingleCallResult(contract, 'getWrappedToken', inputs, {
    blocksPerFetch,
  }) as {
    result?: Awaited<ReturnType<NonNullable<typeof contract>['getWrappedToken']>>
    syncing: boolean
  }

  const wrappedTokenAddress = result?.toString() // convert to a string before using in a hook, to avoid spurious rerenders
  console.log(wrappedTokenAddress, 'wrappedTokenAddress')
  // const allowance = useMemo(
  //   () => (token && rawAmount ? CurrencyAmount.fromRawAmount(token, rawAmount) : undefined),
  //   [token, rawAmount]
  // )
  useEffect(() => setBlocksPerFetch(wrappedTokenAddress == ADDRESS_ZERO ? 1 : undefined), [wrappedTokenAddress])

  return useMemo(() => ({ fewWrappedTokenAddress: wrappedTokenAddress, isSyncing }), [wrappedTokenAddress, isSyncing])
}

export function useUpdateTokenAllowance(
  amount: CurrencyAmount<Token> | undefined,
  spender: string
): () => Promise<{ response: ContractTransaction; info: ApproveTransactionInfo }> {
  const contract = useTokenContract(amount?.currency.address)
  const trace = useTrace()

  return useCallback(async () => {
    try {
      if (!amount) throw new Error('missing amount')
      if (!contract) throw new Error('missing contract')
      if (!spender) throw new Error('missing spender')

      const allowance = amount.equalTo(0) ? '0' : MAX_ALLOWANCE
      const response = await contract.approve(spender, allowance)
      sendAnalyticsEvent(InterfaceEventName.APPROVE_TOKEN_TXN_SUBMITTED, {
        chain_id: amount.currency.chainId,
        token_symbol: amount.currency.symbol,
        token_address: amount.currency.address,
        ...trace,
      })
      return {
        response,
        info: {
          type: TransactionType.APPROVAL,
          tokenAddress: contract.address,
          tokenSymbol: amount.currency.symbol,
          spender,
          amount: allowance,
        },
      }
    } catch (e: unknown) {
      const symbol = amount?.currency.symbol ?? 'Token'
      if (didUserReject(e)) {
        throw new UserRejectedRequestError(`${symbol} token allowance failed: User rejected`)
      }
      throw new Error(`${symbol} token allowance failed: ${e instanceof Error ? e.message : e}`)
    }
  }, [amount, contract, spender, trace])
}

export function useRevokeTokenAllowance(
  token: Token | undefined,
  spender: string
): () => Promise<{ response: ContractTransaction; info: ApproveTransactionInfo }> {
  const amount = useMemo(() => (token ? CurrencyAmount.fromRawAmount(token, 0) : undefined), [token])

  return useUpdateTokenAllowance(amount, spender)
}
